/**
 * Loop brace — contiguous playback/view range over arrange pattern rows.
 * (Product name: “loop brace”; persisted model field: `patternLoop`.)
 * Whole-pattern snap for move / top / bottom resize.
 *
 * Lives in the sticky left rail (see ArrangeBody) so horizontal scroll cannot
 * carry it away — no scroll listeners / transforms.
 */
import { useCallback, useEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { ARRANGE_HEADER_PX, ARRANGE_ROW_PX } from "../../lib/arrangePlayhead";
import { clampPatternLoop, type PatternLoopRange } from "../../lib/patternLoop";

export const ARRANGE_LOOP_COL_PX = 18;

type DragKind = "move" | "resize-top" | "resize-bottom";

type Props = {
  patternCount: number;
  range: PatternLoopRange;
  onChange: (next: PatternLoopRange) => void;
  onContextMenu?: (event: ReactMouseEvent) => void;
};

export function ArrangeLoopBrace({ patternCount, range, onChange, onContextMenu }: Props) {
  const loop = clampPatternLoop(range, patternCount);
  const dragRef = useRef<{
    kind: DragKind;
    originY: number;
    originStart: number;
    originLength: number;
    pointerId: number;
  } | null>(null);

  const commitFromPointer = useCallback(
    (clientY: number, kind: DragKind, originStart: number, originLength: number, originY: number) => {
      const deltaRows = Math.round((clientY - originY) / ARRANGE_ROW_PX);
      if (kind === "move") {
        onChange(clampPatternLoop({ start: originStart + deltaRows, length: originLength }, patternCount));
        return;
      }
      if (kind === "resize-top") {
        const nextStart = originStart + deltaRows;
        const bottom = originStart + originLength - 1;
        const start = Math.max(0, Math.min(bottom, nextStart));
        onChange(clampPatternLoop({ start, length: bottom - start + 1 }, patternCount));
        return;
      }
      const nextEnd = originStart + originLength - 1 + deltaRows;
      const end = Math.max(originStart, Math.min(patternCount - 1, nextEnd));
      onChange(clampPatternLoop({ start: originStart, length: end - originStart + 1 }, patternCount));
    },
    [onChange, patternCount],
  );

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      commitFromPointer(event.clientY, drag.kind, drag.originStart, drag.originLength, drag.originY);
    };
    const onUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [commitFromPointer]);

  function beginDrag(kind: DragKind, event: ReactPointerEvent) {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      kind,
      originY: event.clientY,
      originStart: loop.start,
      originLength: loop.length,
      pointerId: event.pointerId,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  }

  const top = ARRANGE_HEADER_PX + loop.start * ARRANGE_ROW_PX;
  const height = loop.length * ARRANGE_ROW_PX;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[1]"
      aria-hidden={true}
    >
      <div
        className="pointer-events-auto absolute left-[2px] right-[2px] cursor-default rounded-[3px]"
        style={{
          top,
          height,
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--arm) 72%, #1a1208) 0%, color-mix(in srgb, var(--arm) 48%, #120c06) 100%)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.35)",
          opacity: 0.92,
        }}
        onPointerDown={event => beginDrag("move", event)}
        onContextMenu={event => {
          event.preventDefault();
          event.stopPropagation();
          onContextMenu?.(event);
        }}
        title="Loop brace — drag to move, edges to resize · right-click for bounce"
      >
        {/* Decorative grab hint — "=" bars, not interactive. */}
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 flex w-[7px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[2px]"
          aria-hidden={true}
        >
          <span
            className="block h-px w-full rounded-[0.5px]"
            style={{
              background: "color-mix(in srgb, #fff8ee 72%, var(--arm))",
              boxShadow: "0 1px 0 rgba(0,0,0,0.45)",
            }}
          />
          <span
            className="block h-px w-full rounded-[0.5px]"
            style={{
              background: "color-mix(in srgb, #fff8ee 72%, var(--arm))",
              boxShadow: "0 1px 0 rgba(0,0,0,0.45)",
            }}
          />
        </div>
        <div
          className="absolute top-0 right-0 left-0 h-[6px] cursor-ns-resize"
          onPointerDown={event => beginDrag("resize-top", event)}
        />
        <div
          className="absolute right-0 bottom-0 left-0 h-[6px] cursor-ns-resize"
          onPointerDown={event => beginDrag("resize-bottom", event)}
        />
      </div>
    </div>
  );
}
